import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { PlusSmallIcon } from '@heroicons/react/24/outline';
import { ArrowRight, Zap, Headset, CircleDollarSign, Handshake } from 'lucide-react';
import { JsonLd } from '../components/JsonLd';

const sections = [
  {
    icon: Zap,
    title: 'Getting Started',
    faqs: [
      { q: 'How quickly can you start handling our calls?', a: 'We can typically get you set up the same day. We\'ll take time to learn about your business, agree call scripts, and ensure our team is briefed before going live.' },
      { q: 'Do we need to change our phone number?', a: 'No. We simply divert your existing number to us during the hours you choose — daytime, evenings, weekends, or 24/7.' },
      { q: 'Is there a minimum contract length?', a: 'We offer flexible arrangements starting from a rolling monthly contract, with discounts available on longer terms.' },
      { q: 'Can we trial the service before committing?', a: 'Yes. We offer a trial period so you can experience the service firsthand before signing up to a longer arrangement. Get in touch to discuss the options.' },
      { q: 'How do we cancel if we need to?', a: 'We require 30 days\' written notice to cancel. There are no penalties or exit fees — we believe you should stay because you love the service, not because you\'re locked in.' },
    ],
  },
  {
    icon: Headset,
    title: 'Our Services',
    faqs: [
      { q: 'What services does Cygnet provide?', a: 'We provide inbound call handling, virtual receptionist services, email monitoring, and Duty of Care outbound calls. We do not conduct sales or cold calling.' },
      { q: 'Do your agents know our business?', a: 'Yes. We invest time upfront to understand your business, products, services, and FAQs. Your calls are handled by a dedicated team who represent your brand professionally.' },
      { q: 'Can you handle calls out of hours?', a: 'Absolutely. We offer 24/7/365 coverage. Many clients use us specifically for evenings, weekends, and bank holidays.' },
      { q: 'What is a Duty of Care call?', a: 'A Duty of Care call is a proactive outbound call to check in on a customer or service user — for example, following up after a medical appointment, or confirming that a vulnerable customer received their delivery. These are welfare-focused calls, not sales calls.' },
      { q: 'Do you handle sensitive or confidential calls?', a: 'Yes. We regularly handle sensitive calls in healthcare, legal, and financial sectors. All operators are trained in confidentiality protocols and we are fully ICO registered and GDPR compliant.' },
      { q: 'Will callers know they\'re speaking to an outsourced service?', a: 'Only if you want them to. We answer in your business name using your custom script. Most clients\' customers never know — they simply experience a professional, helpful member of your team.' },
    ],
  },
  {
    icon: CircleDollarSign,
    title: 'Pricing & Billing',
    faqs: [
      { q: 'How does pricing work?', a: 'Pricing is based on your call volume and the services you need. We offer transparent per-minute or per-call packages. Visit our Pricing page or get in touch for a tailored quote.' },
      { q: 'Are there setup fees?', a: 'No setup fees on standard packages. We believe you should only pay for what you use.' },
      { q: 'What happens if my call volume increases?', a: 'Our pricing scales with you. We\'ll review usage monthly and ensure you\'re always on the right package.' },
      { q: 'Is it really cheaper than hiring a receptionist?', a: 'Significantly. A full-time UK receptionist costs £22,000–£28,000 per year in salary alone, before NI, pension, holiday cover, and recruitment. Our plans are a fraction of that cost, with no sick days or staff management.' },
    ],
  },
  {
    icon: Handshake,
    title: 'Working Together',
    faqs: [
      { q: 'How do I receive messages from my calls?', a: 'We can deliver messages instantly via SMS, email, or our client portal — or transfer live calls directly to you.' },
      { q: 'Can I listen to call recordings?', a: 'Yes. Call recordings are available to all clients through the client portal, subject to data protection compliance.' },
      { q: 'What industries do you work with?', a: 'We work across Healthcare, Trades & Engineering, Transport & Logistics, Professional Services, IT, Finance, Retail, Manufacturing, Facilities & Field Services, Estate Agents & Property, Legal, Education, Automotive, Energy & Utilities, Security & Alarm Companies, and Charities. If your sector isn\'t listed, get in touch — we can almost certainly help.' },
      { q: 'What happens if your team can\'t answer a call?', a: 'Our service is designed so this rarely happens — we have sufficient staffing to handle your volume. In the unlikely event a call is missed, we have protocols to follow up promptly and notify you immediately.' },
      { q: 'Can you integrate with our existing systems?', a: 'Yes. We regularly work with CRM platforms, booking systems, helpdesk software, and ticketing tools. During onboarding we\'ll agree exactly how we access and update your systems.' },
    ],
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: sections.flatMap(section =>
    section.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    }))
  ),
};

const FAQPage = () => (
  <>
  <JsonLd data={faqSchema} />
  <div className="pb-12">
    {/* Hero */}
    <section className="bg-cygnet-dark text-white py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-3xl px-6 text-center"
      >
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
        <p className="text-xl text-white/80">Everything you need to know about working with Cygnet</p>
      </motion.div>
    </section>

    {/* FAQ sections */}
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-4xl px-6 space-y-16">
        {sections.map((section, si) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: si * 0.1 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-3">
              <section.icon className="w-6 h-6 text-cygnet-dark" />
              {section.title}
            </h2>
            <dl className="space-y-4">
              {section.faqs.map((faq) => (
                <Disclosure key={faq.q} as="div" className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {({ open }) => (
                    <>
                      <DisclosureButton className="flex w-full items-start justify-between text-left px-6 py-5 text-gray-900 hover:bg-gray-50 transition-colors">
                        <span className="text-base font-semibold leading-7">{faq.q}</span>
                        <motion.span
                          animate={{ rotate: open ? 45 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-6 flex h-7 items-center shrink-0"
                        >
                          <PlusSmallIcon className="h-6 w-6" />
                        </motion.span>
                      </DisclosureButton>
                      <AnimatePresence initial={false}>
                        {open && (
                          <DisclosurePanel static as="dd">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <p className="px-6 pb-5 text-base leading-7 text-gray-500">{faq.a}</p>
                            </motion.div>
                          </DisclosurePanel>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </Disclosure>
              ))}
            </dl>
          </motion.div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="py-12 md:py-20 bg-gray-50 border-t border-gray-200">
      <div className="mx-auto max-w-2xl px-6 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Still have questions? Let's talk.</h2>
        <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-cygnet-dark px-8 py-4 text-base font-semibold text-white hover:bg-cygnet-DEFAULT transition-colors">
          Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
  </>
);

export default FAQPage;
